export const SocialCaptionEditing = {
  OFF: 'off',
  OPTIONAL: 'optional',
  REQUIRED: 'required',
} as const;

export type SocialCaptionEditing =
  (typeof SocialCaptionEditing)[keyof typeof SocialCaptionEditing];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseSocialDescriptionBlocks = (
  description: string,
): unknown[] | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(description);
  } catch {
    return null;
  }

  return isRecord(parsed) &&
    parsed.type === 'doc' &&
    Array.isArray(parsed.content)
    ? parsed.content
    : null;
};

const isInlineNodeContent = (node: unknown): boolean =>
  isRecord(node) &&
  (node.type !== 'text' ||
    (typeof node.text === 'string' && node.text.trim() !== ''));

const isBlockContent = (block: unknown): boolean =>
  isRecord(block) &&
  Array.isArray(block.content) &&
  block.content.some(isInlineNodeContent);

export const isSocialDescriptionDoc = (description: unknown): boolean =>
  typeof description === 'string' &&
  parseSocialDescriptionBlocks(description) !== null;

export const hasSocialDescriptionContent = (description: unknown): boolean => {
  if (typeof description !== 'string') {
    return false;
  }

  const blocks = parseSocialDescriptionBlocks(description);

  return blocks ? blocks.some(isBlockContent) : description.trim() !== '';
};

export const resolveSocialCaptionEditing = (
  description: unknown,
  captionEditing: SocialCaptionEditing,
): SocialCaptionEditing =>
  hasSocialDescriptionContent(description)
    ? captionEditing
    : SocialCaptionEditing.REQUIRED;

export const isSocialCaptionEditable = (
  captionEditing: SocialCaptionEditing | null | undefined,
): boolean =>
  captionEditing === SocialCaptionEditing.OPTIONAL ||
  captionEditing === SocialCaptionEditing.REQUIRED;
